import {ComponentProps, CSSProperties, useState} from "react";
import {DialogActions, DialogContent, FormattedDialogTitle} from "@dvrt/graphql-widgets";
import {FormattedFormRow, FormContainer, FormDialogActionButtons} from "@dvrt/graphql-widgets";
import {Dialog, TextField, IconButton} from "@mui/material";
import MultipleLineError from "../../../../MultipleLineError";
import CreateAnotherCheckBox from "../../../../CreateAnotherCheckBox";
import {useForm} from "@/utils/Form";
import ErrorHelper from "../../../../../utils/ErrorHelper";
import {FormattedMessage, MessageDescriptor, defineMessage} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IconProp} from "@fortawesome/fontawesome-svg-core";
import SIPUserFetcher from "./SIPUserFetcher";
import {SaveThirdPartyDeviceMutationVariables, useSaveThirdPartyDeviceMutation} from "../../queries.generated";

type Props = {
    isOpen: boolean;
    close: () => void;
    onSuccess: () => void;
    title: MessageDescriptor;
    initialData: SaveThirdPartyDeviceMutationVariables;
    titleVariables?: ComponentProps<typeof FormattedDialogTitle>["values"];
    cacheKeyPostfix?: string;
};

export default ({isOpen, close, title, initialData, onSuccess, titleVariables, cacheKeyPostfix}: Props) => {
    const [createAnother, setCreateAnother] = useState(false);
    const [success, setSuccess] = useState(false);

    const [userInputType, setUserInputType] = useState("password");
    const [userIcon, setUserIcon] = useState<IconProp>("eye");

    const [passwordInputType, setPasswordInputType] = useState("password");
    const [passwordIcon, setPasswordIcon] = useState<IconProp>("eye");

    const sipUserFetcher = new SIPUserFetcher();

    const form = useForm(
        useSaveThirdPartyDeviceMutation,
        "saveThirdPartyDevice",
        initialData,
        undefined,
        undefined,
        cacheKeyPostfix,
    );

    const errorHelper = new ErrorHelper(form.errors);

    async function submit() {
        if (!(await form.submit())) {
            return;
        }

        setSuccess(true);
        form.reset();

        if (!createAnother) handleClose(true);
    }

    function handleClose(asyncSuccess?: boolean) {
        const actualSuccess = asyncSuccess === null ? success : asyncSuccess;

        close();
        if (actualSuccess) onSuccess();
        form.reset();
    }

    const iconStyle: CSSProperties = {
        marginTop: 7,
    };

    return (
        <Dialog open={isOpen}>
            <FormattedDialogTitle title={title} values={titleVariables}>
                {errorHelper.hasError("limit") && (
                    <p style={{color: "red"}}>
                        <FormattedMessage id="quota_depleted" defaultMessage="Quota depleted" />
                    </p>
                )}
            </FormattedDialogTitle>
            <DialogContent>
                <FormContainer>
                    <FormattedFormRow label={defineMessage({id: "description", defaultMessage: "Description"})}>
                        <TextField
                            variant="standard"
                            id={"thirdparty-device-description"}
                            margin="dense"
                            value={form.values.description}
                            onChange={(event) => form.updateValues({description: event.target.value})}
                            error={errorHelper.hasError("description")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("description")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "sip_username", defaultMessage: "SIP username"})}>
                        <TextField
                            variant="standard"
                            type={userInputType}
                            id={"thirdparty-device-sip-username"}
                            margin="dense"
                            value={form.values.sipUser}
                            onChange={(event) => form.updateValues({sipUser: event.target.value})}
                            error={errorHelper.hasError("sipUser")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("sipUser")} />}
                        />
                        <IconButton
                            style={iconStyle}
                            color="primary"
                            size="small"
                            onClick={() => {
                                setUserIcon(userInputType === "text" ? "eye" : "eye-slash");
                                setUserInputType(userInputType === "text" ? "password" : "text");
                            }}
                        >
                            <FontAwesomeIcon icon={userIcon} style={{minWidth: 25}} />
                        </IconButton>
                        <IconButton
                            style={iconStyle}
                            color="primary"
                            size="small"
                            onClick={async () => {
                                const sipUser = await sipUserFetcher.fetchSipUser();
                                form.updateValues({sipUser: sipUser.username});
                            }}
                        >
                            <FontAwesomeIcon icon="sync-alt" />
                        </IconButton>
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "sip_password", defaultMessage: "SIP password"})}>
                        <TextField
                            variant="standard"
                            id={"thirdparty-device-sip-password"}
                            type={passwordInputType}
                            margin="dense"
                            value={form.values.sipPassword}
                            onChange={(event) => form.updateValues({sipPassword: event.target.value})}
                            error={errorHelper.hasError("sipPassword")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("sipPassword")} />}
                        />
                        <IconButton
                            style={iconStyle}
                            color="primary"
                            size="small"
                            onClick={() => {
                                setPasswordIcon(passwordInputType === "text" ? "eye" : "eye-slash");
                                setPasswordInputType(passwordInputType === "text" ? "password" : "text");
                            }}
                        >
                            <FontAwesomeIcon icon={passwordIcon} style={{minWidth: 25}} />
                        </IconButton>
                        <IconButton
                            style={iconStyle}
                            color="primary"
                            size="small"
                            onClick={async () => {
                                const sipUser = await sipUserFetcher.fetchSipUser();
                                form.updateValues({sipPassword: sipUser.password});
                            }}
                        >
                            <FontAwesomeIcon icon="sync-alt" />
                        </IconButton>
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                {form.values.userId && <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />}
                <FormDialogActionButtons
                    mainId="thirdparty-device-save"
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={handleClose}
                    enabled={form.hasChanged}
                />
            </DialogActions>
        </Dialog>
    );
};
